import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { BillingPlanType } from '@/api/v1/billing/getBillingPlanDetailsApi';
import { convertCentsToDollars } from '@/helpers/formatHelpers';
import { isSingleUsePaymentPlan } from '@/helpers/billingHelpers';
import { UNLIMITED_USERS_LIMIT } from '@/const/shared';

export const getFormattedBillingPlans = (plans: BillingPlanType[]) => {
  if (!plans) return [];

  const groupedPlans = plans.reduce(
    (result, plan) => {
      const { name, billing_frequency } = plan;

      result[name] = result[name] || {};
      result[name][billing_frequency] = plan;

      return result;
    },
    {} as Record<string, Record<string, BillingPlanType>>
  );

  return Object.entries(groupedPlans)
    .map(([name, billingFrequency]) => {
      return {
        name,
        order: billingFrequency.year?.order,
        scheduleType: billingFrequency.year?.schedule_type,
        billingFrequency,
      };
    })
    .sort((a, b) => a.order - b.order);
};

export const getPlanPrice = (plan: BillingPlanType, fractionDigits?: number) => {
  const singleUsePaymentPlan = isSingleUsePaymentPlan(plan.schedule_type);
  const price = singleUsePaymentPlan ? plan.extra_view_price_amount : plan.interval_price_amount;

  return convertCentsToDollars(price, fractionDigits);
};

export const getEnableVerificationText = (enabledIdVerification: boolean) => {
  return enabledIdVerification ? 'ID verification enabled' : 'ID verification disabled';
};

export const getUsersLimitText = (usersLimit: number) => {
  const value = usersLimit === UNLIMITED_USERS_LIMIT ? 'Unlimited' : usersLimit;
  const label = usersLimit === 1 ? 'user' : 'users';

  return (
    <>
      <Typography as='span' color={COLORS.black} fontWeight='semibold' variant='bodySmall'>
        {value}
      </Typography>{' '}
      {label}
    </>
  );
};

export const getPaymentsLimitText = (paymentLimit: number) => {
  const paymentLabel = paymentLimit === 1 ? 'sending' : 'sending';
  const depositLabel = paymentLimit === 1 ? 'receiving' : 'receiving';

  return (
    <>
      <Typography as='span' color={COLORS.black} fontWeight='semibold' variant='bodySmall'>
        {paymentLimit}
      </Typography>{' '}
      {paymentLabel}{' '}
      <Typography as='span' color={COLORS.grey[600]} variant='bodySmall'>
        and/or
      </Typography>{' '}
      {depositLabel}{' '}
      <Typography as='span' color={COLORS.grey[600]} variant='bodySmall'>
        included monthly
      </Typography>
    </>
  );
};

export const getOveragePriceText = (overagePrice) => {
  return (
    <Typography color={COLORS.grey[700]} variant='bodySmall'>
      Overage rate —{' '}
      <Typography as='span' fontWeight='semibold' variant='bodySmall'>
        {overagePrice}
      </Typography>{' '}
      per sending or receiving
    </Typography>
  );
};
